import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { graphqlErrorsToUiErrors } from '../../../api';
import { getDocumentTemplates, getOrganizationsTemplatesByUser, getTemplateFolders } from '../../../api/queries_v2';
import { useTeamInfo } from '../../../team-id-context';
import { useDealId } from '../../../deal-id-context';
import { DocumentTemplateModalTable } from './documentTemplateModalTable';
import { DocumentTemplateModalFilters } from './documentTemplateModalFilters';
import NotFound from '../../not-found';
import ErrorMessage from '../../../components/error-message';
import LoadingSpinner from '../../../components/loading-spinner';
import DocumentTemplateModal from './DocumentTemplateModal';
import TemplateInputFormik from '../../../forms/TemplateInputForm';
import styles from './styles.module.scss';
import { TemplateType } from '../../../consts/forms-types';
import { fuzzyInputSearch } from '../helpers/determineDealsActionModalMessage';
import { getTemplateType } from '../../../shared/templateType';
import {
  createVirtualFolders,
  isVirtualFolderId,
  getTemplatesForVirtualFolder,
  excludeSharedTemplates,
  createVirtualFolderItem,
} from '../../../utils/virtualFolders';

/**
 * Build folder hierarchy from templates' folder data
 * This avoids needing getTemplateFolders permission for organizations
 */
const buildFoldersFromTemplates = (templates: any[]) => {
  const foldersMap = new Map();

  templates.forEach((template) => {
    if (template.folder) {
      // Determine parent folder ID from path
      let parentId = null;
      if (template.folder.path && template.folder.path.length > 1) {
        // If path is ['root', 'subfolder'], parent is 'root'
        // We need to find the folder with name matching the second-to-last path element
        const parentName = template.folder.path[template.folder.path.length - 2];
        // For now, we'll use a simple heuristic - this could be enhanced
        parentId = `parent_${parentName}`;
      }

      foldersMap.set(template.folder.id, {
        id: template.folder.id,
        name: template.folder.name,
        path: template.folder.path,
        parentId: parentId,
      });
    }
  });

  return Array.from(foldersMap.values());
};

/**
 * lease template modal component
 * @param {boolean} isOpen - if modal is open
 * @param {function} onClose - on close handler
 * @param {object} dealData - deal data object
 * @returns {JSX.Element} - the modal componet
 * @constructor
 */
export type DocumentTemplateModalComponentProps = {
  isOpen: boolean;
  onClose: () => void;
  dealData?: any;
  modalFilters?: JSX.Element;
};

export type DocumentTemplateModalType = {
  customTemplate: boolean;
  dealType: string | null;
  description: string;
  hidden: boolean;
  id: string | Number;
  country: string;
  state?: string;
  name: string;
  status: string;
  templateType: string;
  source: string;
  importedAssetUrl?: string;
  team: {
    id?: string;
    name: string;
  };
  sharedFromTeam?: {
    name: string;
  };
  template?: {
    body: string;
  };
  // Draft versioning fields
  draftTemplate?: any;
  hasPendingChanges?: boolean;
  draftUpdatedAt?: string;
  workingTemplate?: any;
  publishedTemplate?: any;
  hasPendingSuggestions?: boolean;
  canPublish?: boolean;
  // hasContent?: boolean; // TODO: Add once backend provides this field
};

export interface ApolloErrorInterface {
  _general: { id: string | number };
}

const DocumentTemplateModalContainer = ({ isOpen, onClose, dealData }: DocumentTemplateModalComponentProps) => {
  const [selectedTemplate, setSelectedTemplate] = React.useState<string | number>('');
  const [filterText, setFilterText] = React.useState('');
  const [selectedCreator, setSelectedCreator] = React.useState<string>('');
  const [creatingDraft, setCreatingDraft] = React.useState(false);
  const { teamId, teamName } = useTeamInfo();
  const dealId = useDealId();
  const [templateSourceTeamUUID, setSelectedTemplateSourceTeamUUID] = React.useState('');
  const [templateSourceTeamName, setSelectedTemplateSourceTeamName] = React.useState('');
  const [currentVirtualFolderId, setCurrentVirtualFolderId] = React.useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = React.useState<string | null>(null);
  const [folderPath, setFolderPath] = React.useState<Array<{ id: string | null; name: string; isVirtual?: boolean }>>([
    { id: null, name: 'Templates' },
  ]);
  const {
    loading: documentTemplatesDefaultLoading,
    data: documentTemplatesDefaultData,
    error: graphqlErrorLoadingDefaultTemplates,
  } = useQuery(getDocumentTemplates, {
    fetchPolicy: 'cache-and-network', // Use cache for instant load, fetch fresh data in background
    variables: {
      teamId: null,
      includeContent: false,
    },
  });

  const {
    loading: documentTemplatesCustomLoading,
    data: documentTemplatesCustomData,
    error: graphqlErrorLoadingCustomTemplates,
  } = useQuery(getDocumentTemplates, {
    fetchPolicy: 'cache-and-network', // Use cache for instant load, fetch fresh data in background
    errorPolicy: 'ignore',
    variables: {
      teamId: teamId,
      includeContent: false,
    },
  });

  const {
    loading: organizationsTemplatesDataLoading,
    data: organizationsTemplatesData,
    error: graphqlErrorOrganizationsTemplatesData,
  } = useQuery(getOrganizationsTemplatesByUser, {
    fetchPolicy: 'cache-and-network', // Use cache for instant load, fetch fresh data in background
    variables: {
      teamId,
      includeContent: false,
    },
  });

  const {
    loading: templateFoldersDataLoading,
    data: templateFoldersData,
    error: templateFoldersError,
  } = useQuery(getTemplateFolders, {
    fetchPolicy: 'cache-and-network', // Use cache for instant load, fetch fresh data in background
    variables: {
      teamId,
      folderType: 'template',
    },
  });

  // Note: Users with organizationsTemplatesByUser access only have access to specific templates,
  // not the full organization folder structure. They don't have folder query permissions.

  const TEMPLATE_STATUS_ACTIVE = 'active';
  const isLoading =
    documentTemplatesDefaultLoading ||
    documentTemplatesCustomLoading ||
    organizationsTemplatesDataLoading ||
    templateFoldersDataLoading;

  const creators = React.useMemo(() => {
    // Combine all templates like the main templates page does
    const allTemplates = [
      ...(documentTemplatesDefaultData?.getDocumentTemplates || []),
      ...(documentTemplatesCustomData?.getDocumentTemplates || []),
      ...(organizationsTemplatesData?.organizationsTemplatesByUser || []),
    ];

    const creatorsSet = new Set<string>();

    allTemplates.forEach((template: any) => {
      // Add regular team names
      if (template?.team?.name) {
        if (template?.team?.id === teamId) {
          creatorsSet.add(`${template.team.name} (Your Team)`);
        } else {
          creatorsSet.add(template.team.name);
        }
      }
      // Add shared template source teams
      if (template?.sharedFromTeam?.name) {
        creatorsSet.add(template.sharedFromTeam.name);
      }
    });

    return Array.from(creatorsSet).sort((a: any, b: any) => {
      // Keep "Your Team" at the top
      if (a.includes('(Your Team)')) return -1;
      if (b.includes('(Your Team)')) return 1;
      return a.localeCompare(b);
    });
  }, [
    documentTemplatesDefaultData,
    documentTemplatesCustomData,
    organizationsTemplatesData,
    teamId,
    teamName,
  ]);

  const selectTemplate = useCallback((templateId: string | number, teamUUID: string, teamName?: string) => {
    setSelectedTemplate(templateId);
    setSelectedTemplateSourceTeamUUID(teamUUID);
    setSelectedTemplateSourceTeamName(teamName || '');
  }, []);

  React.useEffect(() => {
    if (creators.length && !selectedCreator) {
      // Default to user's team (the one with "(Your Team)" suffix)
      const userTeam = creators.find((creator) => creator.includes('(Your Team)'));
      if (userTeam) {
        setSelectedCreator(userTeam);
      } else if (creators.length > 0) {
        // Fallback to first creator if no "Your Team" found
        setSelectedCreator(creators[0]);
      }
    }
  }, [creators, selectedCreator]);

  // Reset navigation state when creator changes
  React.useEffect(() => {
    if (selectedCreator) {
      // Reset folder navigation to root
      setFolderPath([{ id: null, name: 'Templates' }]);
      setCurrentFolderId(null);
      setCurrentVirtualFolderId(null);
      // Clear selected template when switching creators
      setSelectedTemplate('');
      setSelectedTemplateSourceTeamUUID('');
      setSelectedTemplateSourceTeamName('');
    }
  }, [selectedCreator]);

  if (isLoading || creatingDraft) {
    return <LoadingSpinner />;
  }

  if (!documentTemplatesDefaultData && graphqlErrorLoadingDefaultTemplates) {
    const errors = graphqlErrorsToUiErrors(graphqlErrorLoadingDefaultTemplates) as ApolloErrorInterface;
    if (errors._general.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  if (!documentTemplatesCustomData && graphqlErrorLoadingCustomTemplates) {
    const errors = graphqlErrorsToUiErrors(graphqlErrorLoadingCustomTemplates) as ApolloErrorInterface;
    if (errors._general.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  if (!organizationsTemplatesData && graphqlErrorOrganizationsTemplatesData) {
    const errors = graphqlErrorsToUiErrors(graphqlErrorOrganizationsTemplatesData) as ApolloErrorInterface;
    if (errors._general.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }
  /**
   * creat draft api call
   * @param templateId
   * @returns {Promise<void>}
   */
  const isCreatingLeaseDraft = () => {
    setCreatingDraft(true);
  };

  const onModalClose = () => {
    setSelectedTemplate('');
    onClose();
  };

  const { dealType } = dealData;

  const filterByDealType = (template: DocumentTemplateModalType) => {
    return template.dealType ? template.dealType === dealType : template;
  };

  const filterByTemplateType = (template: DocumentTemplateModalType) => {
    return template.templateType === TemplateType.DOCUMENT;
  };

  const filterByStatus = (template: DocumentTemplateModalType) => {
    const templateStatus = template.status.toLocaleLowerCase();
    const isActive = templateStatus === TEMPLATE_STATUS_ACTIVE;

    // Check if template has published content based on template type
    const hasPublishedContent = (() => {
      // For imported templates (PDFs), check if they have imported asset URL
      if (template.source === 'imported') {
        return template.importedAssetUrl != null;
      }

      // For editor templates with active status, they MUST have content
      // (templates only become active when published with content)
      return true;
    })();

    return isActive && hasPublishedContent;
  };

  const filterByCreator = (template: DocumentTemplateModalType) => {
    if (!selectedCreator) return true; // Show all if no creator selected

    // Handle both regular templates and shared templates
    const templateCreatorName = template.team?.name || template.sharedFromTeam?.name;
    const isUserTeam = template.team?.id === teamId;

    // Match creator selection logic
    const expectedCreatorName = isUserTeam ? `${templateCreatorName} (Your Team)` : templateCreatorName;
    const sharedCreatorName = template.sharedFromTeam?.name;

    return selectedCreator === expectedCreatorName || selectedCreator === sharedCreatorName;
  };

  /**
   * verifies service type
   * @param {object} template - the template object
   * @returns {boolean}
   */
  const filterBySearch = (template: DocumentTemplateModalType) => {
    return fuzzyInputSearch({ searchValue: template.name, filteredText: filterText });
  };

  /**
   * filters the displayable templates
   * @param {object[]} templates - available templates array
   * @returns {boolean}
   */
  const filterTemplates = (templates: Array<any>) => {
    return templates.filter(
      (template: DocumentTemplateModalType) =>
        filterByDealType(template) &&
        filterByCreator(template) &&
        filterBySearch(template) &&
        filterByTemplateType(template)
    );
  };

  const filterActiveCustomTemplates = (templates: Array<any>) => {
    return templates.filter(
      (template: DocumentTemplateModalType) =>
        filterByStatus(template) &&
        filterByCreator(template) &&
        filterBySearch(template) &&
        filterByTemplateType(template)
    );
  };

  const filterOrgsTemplates = (templates: [DocumentTemplateModalType]) => {
    return templates.filter(
      (template: DocumentTemplateModalType) =>
        filterByDealType(template) &&
        filterByTemplateType(template) &&
        filterBySearch(template) &&
        filterByCreator(template)
    );
  };

  // Combine ALL templates first without creator filtering
  const allTemplatesWithDuplicates = [
    ...(documentTemplatesCustomData?.getDocumentTemplates || []),
    ...(documentTemplatesDefaultData?.getDocumentTemplates || []),
    ...(organizationsTemplatesData?.organizationsTemplatesByUser || []),
  ];

  // Remove duplicate templates based on template ID
  const uniqueTemplatesMap = new Map();
  allTemplatesWithDuplicates.forEach((template) => {
    if (template && template.id && !uniqueTemplatesMap.has(template.id)) {
      uniqueTemplatesMap.set(template.id, template);
    }
  });

  // Apply all filters (including creator filter) to the combined templates
  const allTemplatesRaw = Array.from(uniqueTemplatesMap.values())
    .filter((template: any) => {
      // Apply all the same filtering logic that was previously split
      const isDefaultTemplate = !template.customTemplate && !template.team?.id;
      const isCustomTemplate = template.customTemplate && template.team?.id === teamId;
      const isOrgTemplate = !isDefaultTemplate && !isCustomTemplate;

      // Filter by status based on template type
      const statusFilter = isDefaultTemplate
        ? filterByDealType(template) && filterByTemplateType(template)
        : isCustomTemplate
          ? filterByStatus(template) && filterByTemplateType(template)
          : filterByDealType(template) && filterByTemplateType(template);

      return statusFilter && filterByCreator(template) && filterBySearch(template);
    })
    .map((el) => {
      const docTemplateType = getTemplateType(el);

      // Add folder path information for search results
      let folderPath = '';
      if (filterText && el.folder && el.folder.path && el.folder.path.length > 0) {
        // Convert folder path array to readable string
        // Skip the first element if it's 'root' or similar
        const pathElements = el.folder.path.filter((p) => p && p.toLowerCase() !== 'root');
        folderPath = pathElements.length > 0 ? pathElements.join(' > ') : '';
      }

      return {
        ...el,
        docTemplateType,
        folderPath: folderPath,
      };
    });

  // Process templates and folders based on navigation and selected creator
  const inVirtualFolder = isVirtualFolderId(currentVirtualFolderId);
  let allTemplates = allTemplatesRaw;
  let virtualFolderItems: any[] = [];
  let realFolderItems: any[] = [];

  // Determine if selected creator is the user's team
  const isUserTeamSelected = selectedCreator.includes('(Your Team)');

  if (inVirtualFolder && currentVirtualFolderId) {
    // Show only templates from the specific virtual folder
    allTemplates = getTemplatesForVirtualFolder(allTemplatesRaw, currentVirtualFolderId);
  } else if (!inVirtualFolder) {
    // At root level or in a real folder - show appropriate folders and templates

    if (isUserTeamSelected) {
      // Show user's own templates and folders
      const userTemplates = excludeSharedTemplates(allTemplatesRaw);
      const templatesInCurrentFolder = filterText
        ? userTemplates // Show all user templates when searching
        : userTemplates.filter((template) => {
            const templateFolderId = template.folder?.id || null;
            return templateFolderId === currentFolderId;
          });

      // Show user's real folders (hide during search)
      if (!filterText) {
        const foldersInCurrentFolder =
          templateFoldersData?.getTemplateFolders?.filter((folder: any) => {
            const folderParentId = folder.parentFolder?.id || null;
            return folderParentId === currentFolderId;
          }) || [];

        // Only show folders that contain at least one valid template
        const foldersWithTemplates = foldersInCurrentFolder.filter((folder: any) => {
          return userTemplates.some((template) => {
            const templateFolderId = template.folder?.id || null;
            const isInThisFolder = templateFolderId === folder.id;
            const passesFilters =
              filterByDealType(template) &&
              filterByCreator(template) &&
              filterByTemplateType(template) &&
              filterByStatus(template);
            return isInThisFolder && passesFilters;
          });
        });

        realFolderItems = foldersWithTemplates.map((folder: any) => ({
          id: folder.id,
          name: folder.name,
          docTemplateType: 'Folder',
          team: { name: selectedCreator }, // Use selected creator name
          isFolder: true,
          isVirtualFolder: false,
        }));

        // Create virtual folder items (only at root level for shared templates)
        if (!currentFolderId) {
          virtualFolderItems = createVirtualFolders(allTemplatesRaw).map((vf) => ({
            ...createVirtualFolderItem(vf),
            isVirtualFolder: true,
            docTemplateType: 'Folder',
            team: { name: vf.sourceTeam.name },
          }));
        }
      } else {
        // Hide folders during search
        realFolderItems = [];
        virtualFolderItems = [];
      }

      allTemplates = templatesInCurrentFolder;
    } else {
      // Selected creator is another workspace or organization
      const templatesFromCreator = allTemplatesRaw.filter((template) => {
        const templateCreatorName = template.team?.name || template.sharedFromTeam?.name;
        return selectedCreator === templateCreatorName;
      });

      // Check if templates have folder information to determine if we should show folder structure
      const hasfolderData = templatesFromCreator.some((template) => template.folder?.id);

      if (hasfolderData) {
        // Show folder structure built from templates (works for both organizations and shared workspaces)
        const folders = buildFoldersFromTemplates(templatesFromCreator);

        // Show folders for current level that contain at least one valid template
        const foldersAtCurrentLevel = folders.filter((folder) => (folder.parentId || null) === currentFolderId);
        const foldersWithTemplates = foldersAtCurrentLevel.filter((folder) => {
          return templatesFromCreator.some((template) => {
            const templateFolderId = template.folder?.id || null;
            const isInThisFolder = templateFolderId === folder.id;
            const passesFilters =
              filterByDealType(template) && filterByCreator(template) && filterByTemplateType(template);
            return isInThisFolder && passesFilters;
          });
        });

        realFolderItems = foldersWithTemplates.map((folder) => ({
          id: folder.id,
          name: folder.name,
          docTemplateType: 'Folder',
          team: { name: selectedCreator },
          isFolder: true,
          isVirtualFolder: false,
        }));

        // Show templates for current level
        allTemplates = filterText
          ? templatesFromCreator // Show all templates when searching
          : templatesFromCreator.filter((template) => {
              const templateFolderId = template.folder?.id || null;
              return templateFolderId === currentFolderId;
            });

        virtualFolderItems = []; // No virtual folders when browsing folder structure
      } else {
        // No folder data available: Show individual templates only
        allTemplates = templatesFromCreator;
        realFolderItems = [];
        virtualFolderItems = [];
      }
    }
  }

  const modalFilters = () => {
    return (
      <div>
        <DocumentTemplateModalFilters
          filterText={filterText}
          setFilterText={setFilterText}
          creators={creators}
          selectedCreator={selectedCreator}
          setSelectedCreator={setSelectedCreator}
        />
        {allTemplates.length || virtualFolderItems.length || realFolderItems.length ? (
          <DocumentTemplateModalTable
            data={[...realFolderItems, ...virtualFolderItems, ...allTemplates]}
            selectTemplate={selectTemplate}
            selectedTemplateId={selectedTemplate}
            isSearching={!!filterText}
            onFolderClick={(folderId: string, folderName: string, isVirtual: boolean) => {
              if (isVirtual) {
                const newPath = [...folderPath, { id: folderId, name: folderName, isVirtual: true }];
                setFolderPath(newPath);
                setCurrentVirtualFolderId(folderId);
                setCurrentFolderId(null); // Clear real folder when entering virtual folder
              } else {
                // Handle real folder navigation
                const newPath = [...folderPath, { id: folderId, name: folderName, isVirtual: false }];
                setFolderPath(newPath);
                setCurrentFolderId(folderId);
                setCurrentVirtualFolderId(null); // Clear virtual folder when entering real folder
              }
            }}
            currentPath={folderPath}
            onNavigateBack={() => {
              if (folderPath.length > 1) {
                const newPath = folderPath.slice(0, -1);
                setFolderPath(newPath);

                // Determine what folder we're navigating back to
                const parentPath = newPath[newPath.length - 1];
                if (parentPath.isVirtual) {
                  setCurrentVirtualFolderId(parentPath.id);
                  setCurrentFolderId(null);
                } else {
                  setCurrentFolderId(parentPath.id);
                  setCurrentVirtualFolderId(null);
                }
              }
            }}
          />
        ) : (
          <div className="py-5">
            <FormattedMessage
              id="lease.template-modal.no-templates"
              defaultMessage="Sorry, there are no available templates. Please contact <a>support@propdocs.com</a> for further assistance."
              values={{ a: (msg: any) => <a href="mailto:support@propdocs.com">{msg}</a> }}
            />
          </div>
        )}
        {selectedTemplate && (
          <div className={styles.templateModalFooterContainer}>
            <div>
              <FormattedMessage id="lease.template.creator" defaultMessage="Document Name" />
            </div>
            <TemplateInputFormik
              dealId={dealId}
              teamId={teamId}
              templateId={selectedTemplate}
              isCreatingLeaseDraft={isCreatingLeaseDraft}
              onClose={onClose}
              templateSourceTeamName={templateSourceTeamName}
              templateSourceTeamId={templateSourceTeamUUID}
            ></TemplateInputFormik>
          </div>
        )}
      </div>
    );
  };

  return <DocumentTemplateModal isOpen={isOpen} onClose={onModalClose} modalFilters={modalFilters()} />;
};

export default DocumentTemplateModalContainer;
